import React from 'react'
import PropTypes from 'prop-types'
import { Text } from 'shared/components'
import { InWinImage } from 'routes/shared/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory, useParams } from 'react-router-dom'
import CustomIcon from 'shared/components/customIcon'
import config from 'config'

import * as styles from './styles'

const getTeamLogo = (team) => {
	if (team?.logo) {
		return team.logo.startsWith('http')
			? team.logo
			: `${config.assets.baseUrl}${team.logo}`
	}
	return null
}

const getTeamAbbreviation = (team) => {
	// First check if there's an abbreviation field
	if (team?.abbreviation) {
		return team.abbreviation.toUpperCase()
	}

	if (team?.teamName) {
		// Generate 3-letter abbreviation from team name
		const words = team.teamName.split(' ')
		if (words.length >= 2) {
			return words.slice(0, 2).map(word => word.charAt(0)).join('') +
				(words[0].length > 1 ? words[0].charAt(1).toUpperCase() : '')
		}
		return team.teamName.substring(0, 3).toUpperCase()
	}

	return 'TBD'
}

const getTeamColor = (teamName) => {
	// Generate a consistent color based on team name
	const colors = [
		'#FF6B35', '#F7931E', '#FFD23F', '#06FFA5', '#118AB2', '#073B4C',
		'#EF476F', '#FFD166', '#06D6A0', '#073B4C', '#F72585', '#4361EE',
		'#4CC9F0', '#7209B7', '#F72585', '#4361EE', '#4CC9F0', '#7209B7'
	]

	if (!teamName) return colors[0]

	let hash = 0
	for (let i = 0; i < teamName.length; i++) {
		hash = teamName.charCodeAt(i) + ((hash << 5) - hash)
	}

	return colors[Math.abs(hash) % colors.length]
}

const TeamCardComponent = ({ team, onToggleFollow }) => {
	const history = useHistory()
	const { eventId } = useParams()

	const teamLogo = getTeamLogo(team)
	const teamAbbreviation = getTeamAbbreviation(team)
	const teamColor = getTeamColor(team?.teamName)
	const teamName = team?.teamName || teamAbbreviation
	// Handle missing followed field - default to false
	const isFollowed = team?.followed === true

	const handleFollowClick = (e) => {
		e.stopPropagation()
		if (onToggleFollow) {
			onToggleFollow(team)
		}
	}

	const handleSeeAllGamesClick = () => {
		if (team?.id && eventId) {
			history.push(`/main/events/${eventId}/teams/${team.id}/schedule`)
		} else {
			console.warn('Cannot navigate to team schedule: missing team ID or event ID')
		}
	}

	return (
		<div css={styles.cardContainer}>
			<div css={styles.cardContent}>
				{/* Team Logo and Name */}
				<div css={styles.teamInfo}>
					<div css={styles.logoContainer}>
						{teamLogo ? (
							<InWinImage
								source={teamLogo}
								alt={teamName}
								css={styles.logoImage}
							/>
						) : (
							<div css={[styles.logoPlaceholder, { backgroundColor: teamColor }]}>
								<Text size={12} bold css={styles.logoText}>
									{teamAbbreviation}
								</Text>
							</div>
						)}
					</div>
					<Text size={12} bold css={styles.teamNameText}>
						{teamAbbreviation}
					</Text>
				</div>

				{/* Bookmark Button - Positioned absolutely like beneficiary card */}
				<div css={styles.actionSection}>
					<button
						css={styles.bookmarkButton}
						onClick={handleFollowClick}
						aria-label={isFollowed ? 'Remove from favorites' : 'Add to favorites'}
					>
						<FontAwesomeIcon
							icon={isFollowed ? 'bookmark' : ['far', 'bookmark']}
							color={isFollowed ? '#00d4aa' : '#ffffff'}
							size="lg"
						/>
					</button>
				</div>
			</div>

			{/* See All Games Link */}
			<button css={styles.seeAllGamesButton} onClick={handleSeeAllGamesClick}>
				<Text size={10} css={styles.seeAllGamesText}>
					See all games
				</Text>
				<CustomIcon icon="angle-right" style={styles.arrowIcon} />
			</button>
		</div>
	)
}

TeamCardComponent.propTypes = {
	team: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		teamName: PropTypes.string,
		abbreviation: PropTypes.string,
		logo: PropTypes.string,
		sport: PropTypes.string,
		followed: PropTypes.bool,
	}).isRequired,
	onToggleFollow: PropTypes.func,
}

export default TeamCardComponent 